import React, { useEffect, useState } from "react";
import {
  Avatar, Box, CircularProgress, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CallIcon from "@mui/icons-material/Call";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import StarIcon from "@mui/icons-material/Star";
import API from "../../API";
import { formatDate, money, HOLD_LABELS, OUTCOME_LABELS, STATUS_LABELS } from "./MarketplaceBase";

/*
 * Full-page marketplace request detail. Styled to match the panel's order
 * detail screen (OrderScreen): #F8FAFC canvas, white 16px-radius cards with
 * the #E8EDF5 border + soft slate shadow, uppercase eyebrow labels and the
 * indigo accent palette. Standalone from the legacy order screens.
 */

const CARD_STYLE = {
  backgroundColor: "#FFFFFF",
  borderRadius: 16,
  border: "1px solid #E8EDF5",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
};
const EYEBROW_STYLE = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "#94A3B8",
  marginBottom: 6,
};
const SECTION_TITLE_STYLE = {
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#0F172A",
};
const HEADER_ICON_BUTTON_STYLE = {
  border: "1px solid #E2E8F0",
  borderRadius: 12,
  width: 42,
  height: 42,
};

const getStatusChipStyle = (status) => {
  if (["completed", "contract_signed", "worked_with"].includes(status))
    return { backgroundColor: "#DCFCE7", color: "#15803D" };
  if (["contract_pending", "mover_selected", "driver_selected"].includes(status))
    return { backgroundColor: "#FEF3C7", color: "#B45309" };
  if (["cancelled", "not_completed"].includes(status))
    return { backgroundColor: "#FEE2E2", color: "#B91C1C" };
  return { backgroundColor: "#EEF2FF", color: "#4F46E5" };
};

const CONTRACT_STATUS_LABELS = {
  draft: "Taslak",
  awaiting_customer: "Müşteri imzası bekleniyor",
  awaiting_driver: "Sürücü imzası bekleniyor",
  customer_signed: "Müşteri imzaladı",
  driver_signed: "Sürücü imzaladı",
  fully_signed: "İki taraf da imzaladı",
  void: "Geçersiz",
};
const QUOTE_STATUS_LABELS = {
  active: "Aktif",
  selected: "Seçildi",
  not_selected: "Seçilmedi",
  withdrawn: "Geri çekildi",
  expired: "Süresi doldu",
  closed: "Kapandı",
};
const PAYMENT_LABELS = { cash: "Nakit", card: "Kredi kartı", either: "Fark etmez" };
const PACKED_LABELS = { ready: "Paketli ve hazır", needed: "Paketleme gerekli" };
const VEHICLE_LABELS = {
  doblo: "Doblo", panel_van: "Panelvan", pickup_truck: "Kamyonet",
  "6_wheel": "6 Teker Kamyon", "8_wheel": "8 Teker Kamyon",
  "10_wheel": "10 Teker Kamyon", lorry: "Tır",
};
const ITEM_LABELS = {
  sofa: "3'lü koltuk", refrigerator: "Buzdolabı", double_bed: "Çift kişilik yatak",
  wardrobe: "Gardırop", television: "Televizyon", armchair: "Tekli koltuk",
  washing_machine: "Çamaşır makinesi", dining_table: "Yemek masası", other: "Diğer",
};
const RANGE_LABELS = { within_week: "1 hafta içinde", within_month: "1 ay içinde", over_month: "1 aydan sonra" };
const INSURANCE_LABELS = { insured: "Sigortalı", uninsured: "Sigortasız", compare: "İkisini karşılaştır" };
const label = (map, value) => (value ? map[value] || value : "-");

function StatusPill({ status }) {
  return (
    <div
      style={{
        ...getStatusChipStyle(status),
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {STATUS_LABELS[status] || status || "-"}
    </div>
  );
}

function SectionCard({ icon, iconBackground, iconColor, title, children, style }) {
  return (
    <div style={{ ...CARD_STYLE, display: "flex", flexDirection: "column", ...style }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "16px 20px",
          borderBottom: "1px solid #EEF2F7",
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            backgroundColor: iconBackground || "#EEF2FF",
            color: iconColor || "#4F46E5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <Typography style={SECTION_TITLE_STYLE}>{title}</Typography>
      </div>
      <div style={{ padding: "8px 20px 16px 20px", flex: 1 }}>{children}</div>
    </div>
  );
}

function DetailRow({ label: rowLabel, value, last }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 0",
        borderBottom: last ? "none" : "1px solid #F1F5F9",
      }}
    >
      <Typography style={{ fontSize: 13, color: "#64748B", flexShrink: 0 }}>{rowLabel}</Typography>
      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", textAlign: "right", overflowWrap: "anywhere", minWidth: 0 }}>
        {value ?? "-"}
      </Typography>
    </div>
  );
}

function DriverLink({ driverId, name, history }) {
  if (!driverId) return <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{name || "-"}</Typography>;
  return (
    <div
      style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      onClick={() => history.push(`/driver/${driverId}`)}
    >
      <Avatar sx={{ width: 26, height: 26, bgcolor: "#EEF2FF", color: "#4F46E5", fontSize: 12, fontWeight: 700 }}>
        {(name || "?").charAt(0).toUpperCase()}
      </Avatar>
      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{name || "-"}</Typography>
      <OpenInNewIcon sx={{ fontSize: 14, color: "#64748B" }} />
    </div>
  );
}

const headCellSx = {
  fontSize: 12,
  fontWeight: 800,
  color: "#64748B",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: "1px solid #EEF2F7",
  whiteSpace: "nowrap",
};
const bodyCellSx = { fontSize: 13, color: "#0F172A", borderBottom: "1px solid #F1F5F9" };

export default function MarketplaceRequestScreen({ serviceType, history, match }) {
  const requestId = match?.params?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    API.fetchClient("panel/getmarketplacerequest", { serviceType, requestId }).then((result) => {
      setLoading(false);
      if (result?.request) setData(result);
    });
  };
  useEffect(() => { if (requestId) load(); }, [requestId, serviceType]);

  const isIntercity = serviceType === "intercity";
  const payload = data?.request?.payload || {};
  const twoCol = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
    gap: 20,
    marginTop: 20,
    alignItems: "stretch",
  };

  return (
    <div style={{ display: "block", width: "100%", backgroundColor: "#F8FAFC", flexShrink: 0, paddingBottom: 80 }}>
      <div style={{ width: "100%", padding: "0 12px", boxSizing: "border-box", maxWidth: "100%", overflowX: "hidden" }}>
        {/* Header card — same pattern as the order detail screen */}
        <div
          style={{
            ...CARD_STYLE,
            marginTop: 24,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 20,
            padding: "20px 24px",
          }}
        >
          <Tooltip title="Geri">
            <IconButton style={HEADER_ICON_BUTTON_STYLE} onClick={() => history.goBack()}>
              <ArrowBackIcon sx={{ fontSize: 20, color: "#334155" }} />
            </IconButton>
          </Tooltip>
          <div style={{ flex: 1, minWidth: 160 }}>
            <Typography style={EYEBROW_STYLE}>{isIntercity ? "Şehirlerarası Talep" : "Evden Eve Talep"}</Typography>
            <Typography
              style={{ fontSize: 24, lineHeight: 1.1, fontWeight: 800, letterSpacing: "-0.02em", color: "#0F172A" }}
            >
              #{requestId ? requestId.slice(-8).toUpperCase() : "-"}
            </Typography>
            {data?.request?.createdAt ? (
              <Typography style={{ marginTop: 6, color: "#64748B", fontSize: 13 }}>
                Oluşturulma: {formatDate(data.request.createdAt)}
              </Typography>
            ) : null}
          </div>
          <div>
            <Typography style={EYEBROW_STYLE}>Durum</Typography>
            {data ? <StatusPill status={data.request.status} /> : <Typography style={{ color: "#94A3B8" }}>-</Typography>}
            {data?.request?.closureReason ? (
              <Typography style={{ marginTop: 6, color: "#64748B", fontSize: 12, fontWeight: 600 }}>
                {OUTCOME_LABELS[data.request.closureReason] || data.request.closureReason}
              </Typography>
            ) : null}
          </div>
          <Tooltip title="Yenile">
            <IconButton style={HEADER_ICON_BUTTON_STYLE} onClick={load}>
              <RefreshIcon sx={{ fontSize: 20, color: "#334155" }} />
            </IconButton>
          </Tooltip>
        </div>

        {!data || loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>
        ) : (
          <>
            <div style={twoCol}>
              <SectionCard
                icon={<RouteOutlinedIcon sx={{ fontSize: 19 }} />}
                title={isIntercity ? "Rota ve Yük" : "Rota ve Ev Bilgileri"}
              >
                {isIntercity ? (
                  <>
                    <DetailRow label="Alış" value={payload.route?.pickup?.address?.label} />
                    <DetailRow label="Teslimat" value={payload.route?.delivery?.address?.label} />
                    <DetailRow label="Mod" value={payload.mode === "one_item" ? "Tek eşya" : "Büyük yük"} />
                    {payload.requestedVehicle ? <DetailRow label="İstenen araç" value={label(VEHICLE_LABELS, payload.requestedVehicle)} /> : null}
                    <DetailRow label="Tarih" value={payload.schedule?.selectedDate || label(RANGE_LABELS, payload.schedule?.flexibleRange)} />
                    <DetailRow label="Ödeme tercihi" value={label(PAYMENT_LABELS, payload.paymentPreference)} />
                    {payload.mode === "one_item" ? (
                      <>
                        <DetailRow label="Eşya" value={label(ITEM_LABELS, payload.cargo?.itemType)} />
                        <DetailRow label="Ölçüler" value={payload.cargo?.dimensions} />
                        <DetailRow label="Ağırlık" value={payload.cargo?.weight} />
                      </>
                    ) : (
                      <DetailRow label="Yük açıklaması" value={payload.cargo?.description} />
                    )}
                    <DetailRow label="Paket durumu" value={label(PACKED_LABELS, payload.cargo?.packedStatus)} last />
                  </>
                ) : (
                  <>
                    <DetailRow label="Alış" value={payload.pickup?.exactAddress || payload.pickup?.label} />
                    <DetailRow label="Teslimat" value={payload.dropoff?.exactAddress || payload.dropoff?.label} />
                    <DetailRow label="Ev tipi" value={payload.homeType} />
                    <DetailRow label="Tarih" value={payload.movingDate?.startsAt ? formatDate(payload.movingDate.startsAt) : label(RANGE_LABELS, payload.movingDate?.approximateTiming)} />
                    <DetailRow label="Ödeme tercihi" value={label(PAYMENT_LABELS, payload.paymentPreference)} />
                    <DetailRow label="Sigorta tercihi" value={label(INSURANCE_LABELS, payload.logistics?.insurancePreference)} last />
                  </>
                )}
              </SectionCard>

              <SectionCard
                icon={<PersonOutlineIcon sx={{ fontSize: 19 }} />}
                title="Müşteri"
                iconBackground="#ECFDF5"
                iconColor="#059669"
              >
                <DetailRow label="Ad" value={data.customer?.name} />
                <DetailRow label="Telefon" value={data.customer?.phone} last={!data.customer?.email} />
                {data.customer?.email ? <DetailRow label="E-posta" value={data.customer.email} last /> : null}
              </SectionCard>

              <SectionCard
                icon={<AssignmentOutlinedIcon sx={{ fontSize: 19 }} />}
                title="Zaman Çizelgesi"
                iconBackground="#FEF6E7"
                iconColor="#D97706"
              >
                <DetailRow label="Oluşturulma" value={formatDate(data.request.createdAt)} />
                <DetailRow label="Teklif penceresi" value={`${formatDate(data.request.quoteWindowStartsAt)} → ${formatDate(data.request.quoteWindowEndsAt)}`} />
                {data.request.closedAt ? <DetailRow label="Kapanış" value={formatDate(data.request.closedAt)} /> : null}
                <DetailRow label="Sonuç" value={data.request.outcome ? (OUTCOME_LABELS[data.request.outcome] || data.request.outcome) : "-"} last />
              </SectionCard>
            </div>

            <SectionCard
              icon={<LocalOfferOutlinedIcon sx={{ fontSize: 19 }} />}
              title={`Teklifler (${data.quotes.length})`}
              style={{ marginTop: 20 }}
            >
              {data.quotes.length === 0 ? (
                <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Henüz teklif yok.</Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Sürücü</TableCell>
                        <TableCell sx={headCellSx}>Telefon</TableCell>
                        <TableCell sx={headCellSx}>Fiyat</TableCell>
                        <TableCell sx={headCellSx}>Araç</TableCell>
                        <TableCell sx={headCellSx}>Durum</TableCell>
                        <TableCell sx={headCellSx}>Bloke / Ücret</TableCell>
                        <TableCell sx={headCellSx}>Tarih</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.quotes.map((quote) => (
                        <TableRow
                          key={quote.quoteId}
                          sx={data.request.selectedQuoteId === quote.quoteId ? { backgroundColor: "#F0FDF4" } : null}
                        >
                          <TableCell sx={bodyCellSx}>
                            <DriverLink driverId={quote.driverId} name={quote.driverName} history={history} />
                            {quote.driverRating != null ? (
                              <Typography style={{ fontSize: 12, color: "#B45309", fontWeight: 700, marginTop: 2 }}>★ {quote.driverRating}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{quote.driverPhone}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 800 }}>{money(quote.price, quote.currency)}</TableCell>
                          <TableCell sx={bodyCellSx}>{label(VEHICLE_LABELS, quote.vehicle)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {data.request.selectedQuoteId === quote.quoteId ? "Seçildi" : label(QUOTE_STATUS_LABELS, quote.status)}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {quote.holdAmount != null ? `${money(quote.holdAmount)} · ${HOLD_LABELS[quote.holdStatus] || quote.holdStatus || "-"}` : "-"}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{formatDate(quote.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </SectionCard>

            <div style={twoCol}>
              <SectionCard
                icon={<CallIcon sx={{ fontSize: 19 }} />}
                title={`Müşteri Aramaları (${data.contacts.length})`}
                iconBackground="#EFF6FF"
                iconColor="#2563EB"
              >
                {data.contacts.length === 0 ? (
                  <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Müşteri henüz hiçbir sürücüyü aramadı.</Typography>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headCellSx}>Sürücü</TableCell>
                          <TableCell sx={headCellSx}>Ücret</TableCell>
                          <TableCell sx={headCellSx}>Konuşma</TableCell>
                          <TableCell sx={headCellSx}>İade</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.contacts.map((contact) => (
                          <TableRow key={contact.contactId}>
                            <TableCell sx={bodyCellSx}>
                              <DriverLink driverId={contact.driverId} name={contact.driverName} history={history} />
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {contact.feeCapturedAt ? (
                                <span style={{ backgroundColor: "#DCFCE7", color: "#15803D", padding: "3px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  Tahsil · {formatDate(contact.feeCapturedAt)}
                                </span>
                              ) : (
                                <span style={{ backgroundColor: "#F1F5F9", color: "#64748B", padding: "3px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  Tahsil edilmedi
                                </span>
                              )}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>{contact.totalCallSeconds || 0} sn</TableCell>
                            <TableCell sx={bodyCellSx}>{contact.refundRequestedAt ? (contact.refundDecisionAt ? "Karar verildi" : "İncelemede") : "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </SectionCard>

              {data.selection ? (
                <SectionCard
                  icon={<LocalShippingIcon sx={{ fontSize: 19 }} />}
                  title="Seçim"
                  iconBackground="#ECFDF5"
                  iconColor="#059669"
                >
                  <div style={{ padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                    <Typography style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>Nakliyeci</Typography>
                    <DriverLink driverId={data.selection.driverId} name={data.selection.moverName} history={history} />
                  </div>
                  <DetailRow label="Anlaşılan fiyat" value={money(data.selection.agreedPrice)} />
                  <DetailRow label="Taşınma tarihi" value={formatDate(data.selection.agreedMoveStartsAt)} last />
                </SectionCard>
              ) : null}

              {data.contract ? (
                <SectionCard icon={<DescriptionOutlinedIcon sx={{ fontSize: 19 }} />} title="Sözleşme">
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                    {data.contract.narrativeHtml ? (
                      <button
                        type="button"
                        onClick={() => {
                          const win = window.open("", "_blank");
                          if (win) { win.document.write(data.contract.narrativeHtml); win.document.close(); }
                        }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#0F172A", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        Sözleşmeyi Görüntüle
                      </button>
                    ) : null}
                    {data.contract.pdfUrl ? (
                      <button
                        type="button"
                        onClick={() => window.open(data.contract.pdfUrl, "_blank")}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "none", backgroundColor: "#4F46E5", color: "#FFFFFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                      >
                        PDF İndir
                      </button>
                    ) : null}
                  </div>
                  <DetailRow label="Sürüm" value={String(data.contract.version)} />
                  <DetailRow label="Durum" value={label(CONTRACT_STATUS_LABELS, data.contract.status)} />
                  <DetailRow label="Müşteri imzası" value={data.contract.customerSignedAt ? `SMS · ${formatDate(data.contract.customerSignedAt)}` : "Bekliyor"} />
                  <DetailRow label="Sürücü imzası" value={data.contract.driverSignedAt ? `SMS · ${formatDate(data.contract.driverSignedAt)}` : "Bekliyor"} />
                  <DetailRow label="PDF" value={data.contract.pdfAvailable ? "Hazır" : "Henüz oluşturulmadı"} />
                  <DetailRow label="Belge özeti (SHA-256)" value={data.contract.narrativeSha256 ? `${data.contract.narrativeSha256.slice(0, 18)}…` : "-"} last />
                </SectionCard>
              ) : null}

              {data.review ? (
                <SectionCard
                  icon={<StarIcon sx={{ fontSize: 19 }} />}
                  title="Müşteri Değerlendirmesi"
                  iconBackground="#FEF6E7"
                  iconColor="#D97706"
                >
                  <DetailRow label="Puan" value={`★ ${data.review.rating} / 5`} />
                  {data.review.comment ? <DetailRow label="Yorum" value={data.review.comment} /> : null}
                  <DetailRow label="Tarih" value={formatDate(data.review.createdAt)} last />
                </SectionCard>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
